import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const DAYS_KINDS = ["shipment", "delivery"];

const TRACKING_PROMISE = "An email with your tracking number will be sent as soon as your order ships.";

function previewSentence(opt, days) {
  if (!opt) return "";
  if (!DAYS_KINDS.includes(opt.kind) || !days) return opt.sentence || "";
  const d = days.replace("-", "–");
  const unit = opt.business ? "business days" : "days";
  const core = opt.kind === "shipment"
    ? `It will be shipped within the next ${d} ${unit}, and delivery follows shortly after shipment.`
    : `Your package is expected to be delivered within approximately ${d} ${unit}.`;
  return `${core} ${TRACKING_PROMISE}`;
}

// 📨 Status email — picks the saved estimate for the order's CRM stage (or manual days) and sends at once.
export function StatusEmailButton({ order, className = "" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [days, setDays] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["status-email-suggest", order.id],
    queryFn: async () => (await api.get(`/orders/${order.id}/status-email/suggest`)).data,
    enabled: open,
  });
  useEffect(() => { if (data && !key) setKey(data.suggested_key || ""); }, [data, key]);
  const opt = (data?.options || []).find((x) => x.key === key);
  const customAllowed = !!opt && DAYS_KINDS.includes(opt.kind);
  const daysOk = !days.trim() || /^\s*\d{1,3}\s*(-\s*\d{1,3})?\s*$/.test(days.replace("–", "-"));
  const payload = () => ({ option_key: key, days: customAllowed && days.trim() ? days.trim() : null });
  const [review, setReview] = useState(null); // full rendered email awaiting confirmation
  const preview = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/status-email/preview`, payload())).data,
    onSuccess: (r) => setReview(r),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not build the email preview"),
  });
  const send = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/status-email`, payload())).data,
    onSuccess: (r) => {
      toast.success(`Status email sent to ${order.customer?.email} · ${r.estimate}`);
      qc.invalidateQueries({ queryKey: ["order", order.id] });
      qc.invalidateQueries({ queryKey: ["order-emails", order.id] });
      qc.invalidateQueries({ queryKey: ["status-email-suggest", order.id] });
      setOpen(false); setDays(""); setReview(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send the status email"),
  });
  const sentAt = order.status_email_sent_at ? new Date(order.status_email_sent_at).toLocaleDateString() : null;
  return (
    <>
      <Button data-testid="status-email-btn" variant="outline" className={`border-slate-400 whitespace-nowrap ${className}`} onClick={() => { setKey(""); setOpen(true); }}
        title={sentAt ? `Status email already sent ${sentAt} · ${order.status_email_estimate || ""}` : "Send the client a status update with the estimate for this stage"}>
        📨 Status email{sentAt ? " ✓" : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="status-email-dialog">
          <DialogHeader>
            <DialogTitle>📨 Status email — {order.customer?.name || "client"}</DialogTitle>
            <DialogDescription>To {order.customer?.email || "—"}. Pick the stage (or type days), then review the full email before it is sent.</DialogDescription>
          </DialogHeader>
          {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
          {data && (
            <div className="space-y-3">
              {data.last_sent && (
                <div className="text-[11px] rounded border border-amber-200 bg-amber-50 text-amber-800 px-2 py-1" data-testid="status-email-last-sent">
                  Already sent {new Date(data.last_sent.sent_at).toLocaleString()} · {data.last_sent.status_estimate || data.last_sent.subject}
                </div>
              )}
              <div>
                <Label className="text-xs">CRM status {data.suggested_key && key === data.suggested_key ? "(suggested)" : ""}</Label>
                <Select value={key} onValueChange={(v) => setKey(v)}>
                  <SelectTrigger data-testid="status-email-option" className="mt-1"><SelectValue placeholder="Pick the status…" /></SelectTrigger>
                  <SelectContent>
                    {data.options.map((x) => (
                      <SelectItem key={x.key} value={x.key} data-testid={`status-email-option-${x.key}`}>
                        {x.crm_label} — {x.days ? `${x.days.replace("-", "–")} ${x.business ? "business days" : "days"}` : x.kind === "carrier" ? "verified carrier ETA" : "no estimate"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {customAllowed && (
                <div>
                  <Label className="text-xs">Manual days (optional) — e.g. 12 or 9-13</Label>
                  <Input data-testid="status-email-days" value={days} onChange={(e) => setDays(e.target.value)} placeholder={opt.days.replace("-", "–")} className="mt-1" />
                  {!daysOk && <p className="text-[11px] text-rose-700 mt-1">Type a number or a range like 9-13</p>}
                </div>
              )}
              {opt?.kind === "carrier" && (
                data.final_tracking
                  ? <div className="text-[11px] rounded border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-1" data-testid="status-email-final-tracking">Final Shopify tracking: <b>{data.final_tracking.number}</b>{data.final_tracking.carrier ? ` · ${data.final_tracking.carrier}` : ""}</div>
                  : <div className="text-[11px] rounded border border-rose-200 bg-rose-50 text-rose-800 px-2 py-1" data-testid="status-email-no-tracking">Shopify has no final tracking number for this order yet — fulfil it (buy the label) first.</div>
              )}
              {opt && (
                <div className="text-xs rounded border border-slate-200 bg-slate-50 px-3 py-2 space-y-1" data-testid="status-email-preview">
                  <div className="text-[11px] text-slate-500">Client reads · <span className="font-mono">{data.subject}</span></div>
                  <p>Hello {order.customer?.name?.split(" ")[0] || "there"},</p>
                  <p>Thank you for your order with Leprecon+. Here is a quick update on your Order #{String(order.shopify_order_number || order.po_number || "").replace(/^#/, "")}.</p>
                  <p>{opt.status_line} <b>{previewSentence(opt, daysOk && days.trim() ? days.trim().replace(/\s/g, "") : "")}</b></p>
                  <p>If you have any questions, simply reply to this email — we're always happy to help.</p>
                  <p className="text-slate-500">Warm regards, Leprecon Plus Team</p>
                </div>
              )}
              <Button data-testid="status-email-review" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={!key || !daysOk || preview.isPending || !order.customer?.email || (opt?.kind === "carrier" && !data.final_tracking)} onClick={() => preview.mutate()}>
                {preview.isPending ? "Preparing…" : "Review full email →"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!review} onOpenChange={(v) => { if (!v) setReview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="status-email-review-dialog">
          <DialogHeader>
            <DialogTitle>Review before sending</DialogTitle>
            <DialogDescription>This is the exact email the client will receive. Nothing is sent until you press Send.</DialogDescription>
          </DialogHeader>
          {review && (
            <div className="space-y-2">
              <div className="text-xs text-slate-600 grid grid-cols-[60px_1fr] gap-x-2 gap-y-0.5">
                <span className="text-slate-400">To</span><span className="font-mono" data-testid="status-email-review-to">{review.to}</span>
                <span className="text-slate-400">Subject</span><span className="font-semibold" data-testid="status-email-review-subject">{review.subject}</span>
              </div>
              <div className="rounded border border-slate-200 bg-white max-h-[55vh] overflow-auto">
                <iframe title="Status email preview" srcDoc={review.body_html} sandbox="" className="w-full h-[55vh] border-0" data-testid="status-email-review-body" />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setReview(null)} data-testid="status-email-review-back">← Back / edit</Button>
                <Button data-testid="status-email-send" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={send.isPending} onClick={() => send.mutate()}>
                  {send.isPending ? "Sending…" : `📨 Send to ${review.to}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
